/**
 * Rating Color Utilities
 *
 * Functions to determine colors based on rating grades.
 * Used by GalaxyScoreDisplay and other rating components.
 */

import { SCORE_HEX_COLORS } from '../constants/colors';

// ============================================
// Rating Scale Constants
// ============================================

/**
 * Full rating scale from lowest to highest
 * Index position determines rating quality
 */
export const ratingScale = [
  'C', 'CC', 'CCC',
  'B-', 'B', 'B+',
  'BB-', 'BB', 'BB+',
  'BBB-', 'BBB', 'BBB+',
  'A-', 'A', 'A+',
  'AA-', 'AA', 'AA+',
  'AAA'
];

/**
 * Rating thresholds for color assignment
 */
export const ratingThresholds = {
  excellent: 16,  // AA and above (index >= 16)
  good: 13,       // A and above (index >= 13)
  adequate: 10,   // BBB and above (index >= 10)
  moderate: 7,    // BB and above (index >= 7)
  // Below 7 is considered weak
};

// ============================================
// Utility Functions
// ============================================

/**
 * Get the index position of a rating in the rating scale
 * Higher index = better rating
 */
export function getRatingIndex(rating: string): number {
  return ratingScale.indexOf(rating);
}

/**
 * Get the CSS color variable for a rating based on its quality
 */
export function getRatingColor(rating: string): string {
  const index = getRatingIndex(rating);
  if (index >= ratingThresholds.excellent) return 'var(--rating-aaa)';
  if (index >= ratingThresholds.good) return 'var(--rating-aa)';
  if (index >= ratingThresholds.adequate) return 'var(--rating-bbb)';
  if (index >= ratingThresholds.moderate) return 'var(--rating-bb)';
  return 'var(--rating-below)';
}

/**
 * Get a raw hex color for a rating (for SVG elements)
 */
export function getRatingHexColor(rating: string): string {
  const index = getRatingIndex(rating);
  if (index >= ratingThresholds.excellent) return SCORE_HEX_COLORS.excellent;
  if (index >= ratingThresholds.good) return SCORE_HEX_COLORS.good;
  if (index >= ratingThresholds.adequate) return SCORE_HEX_COLORS.adequate;
  if (index >= ratingThresholds.moderate) return SCORE_HEX_COLORS.moderate;
  return SCORE_HEX_COLORS.weak;
}

/**
 * Get color based on score ratio (score / maxScore)
 */
export function getScoreColor(score: number, maxScore: number): string {
  const ratio = score / maxScore;
  if (ratio >= 0.8) return SCORE_HEX_COLORS.excellent;
  if (ratio >= 0.6) return SCORE_HEX_COLORS.adequate;
  if (ratio >= 0.4) return SCORE_HEX_COLORS.moderate;
  return SCORE_HEX_COLORS.weak;
}
